/**
 * SeanceRecap.jsx — Page publique de récap d'une Séance Live.
 *
 * Route : /seance-recap (publique, accessible sans auth)
 *
 * Lit le payload depuis l'URL fragment (#data=...) pour reconstituer une
 * synthèse de séance partagée par l'enseignant via QR code ou lien.
 *
 * Affichage : score, critères acquis / à retravailler, notes, mini-schéma.
 * Action : possibilité d'exporter en PDF (réutilise exportSeancePdf).
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, Target, ListChecks, PenLine,
  CheckCircle2, XCircle, FileDown, AlertCircle, Quote,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import useSeo from '../hooks/useSeo';
import { readSeanceFromUrl } from '../utils/seanceShare';
import { getLivretItem } from '../data/teacher/evalInference';
import { getObjectifConcis } from '../data/teacher/objectifsConcis';
import { getBriefingSchema } from '../data/teacher/briefingSchemas';
import { exportSeancePdf } from '../utils/exportSeancePdf';

const CATEGORIE_COLORS = {
  C1: '#0891b2',
  C2: '#7c3aed',
  C3: '#dc2626',
  C4: '#16a34a',
};

function computeStats(state) {
  const statuts = Object.values(state?.critereStatuts || {});
  const okCount = statuts.filter((s) => s === 'ok').length;
  const koCount = statuts.filter((s) => s === 'ko').length;
  const totalTested = okCount + koCount;
  const okRatio = totalTested > 0 ? Math.round((okCount / totalTested) * 100) : null;
  return { okCount, koCount, totalTested, okRatio };
}

function formatDateFr(iso) {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch {
    return '';
  }
}

export default function SeanceRecap() {
  const payload = useMemo(() => readSeanceFromUrl(), []);
  const code = payload?.code;
  const state = payload?.state;
  const livret = useMemo(() => (code ? getLivretItem(code) : null), [code]);
  const concis = useMemo(() => (code ? getObjectifConcis(code) : null), [code]);
  const stats = useMemo(() => computeStats(state || {}), [state]);
  const categorie = code ? code.split('-')[0] : 'C1';
  const accent = CATEGORIE_COLORS[categorie] || '#0891b2';
  const Schema = code ? getBriefingSchema(code) : null;
  const phraseClef = livret?.lecon?.phraseClef || null;

  useSeo({
    title: code
      ? `Récap séance ${code} — Code 2 Roues`
      : 'Récap séance partagée — Code 2 Roues',
    description: 'Synthèse d\'une séance de conduite moto partagée par ton moniteur : objectif, acquis, axes à retravailler, notes.',
    path: '/seance-recap',
  });

  const handleExportPdf = () => {
    try {
      const filename = exportSeancePdf({
        code, livret, concis, phraseClef, state, stats, accent,
      });
      toast.success('PDF exporté', { description: filename });
    } catch (err) {
      console.error('Export PDF échoué', err);
      toast.error('Export PDF impossible');
    }
  };

  /* ── État vide : pas de payload ou décodage raté ─────────────── */
  if (!payload || !livret) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="seance-recap-empty">
        <div className="max-w-md w-full rounded-2xl border-2 border-dashed border-border p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-muted flex items-center justify-center mb-3">
            <AlertCircle className="w-7 h-7 text-muted-foreground" />
          </div>
          <h1 className="text-xl font-black mb-2">Lien de récap invalide</h1>
          <p className="text-sm text-muted-foreground mb-4">
            Le lien partagé est expiré, incomplet ou modifié. Demande à ton moniteur
            de le régénérer depuis l&apos;application.
          </p>
          <Button asChild variant="outline" className="gap-2">
            <Link to="/">
              <ArrowLeft className="w-4 h-4" /> Retour à l&apos;accueil
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const criteres = concis?.criteresPrecis || [];
  const okList = criteres.map((c, i) => ({ c, i })).filter(({ i }) => state.critereStatuts?.[i] === 'ok');
  const koList = criteres.map((c, i) => ({ c, i })).filter(({ i }) => state.critereStatuts?.[i] === 'ko');

  return (
    <div className="min-h-screen pb-12" data-testid="seance-recap-page">
      {/* Bandeau header */}
      <div
        className="px-4 pt-6 pb-12 text-white"
        style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 60%, ${accent}aa 100%)` }}
      >
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest opacity-80 hover:opacity-100 transition-opacity mb-4">
            <ArrowLeft className="w-3.5 h-3.5" /> Code 2 Roues
          </Link>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur px-3 py-1 text-[10px] font-black uppercase tracking-widest mb-3">
            REMC · {code}
          </div>
          <h1
            className="text-3xl sm:text-4xl font-black leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            data-testid="recap-intitule"
          >
            {livret.intitule}
          </h1>
          {state.updatedAt && (
            <p className="text-xs font-bold opacity-80 mt-2">
              Séance du {formatDateFr(state.updatedAt)}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 -mt-6 space-y-4">
        {/* Carte score */}
        <div className="rounded-2xl bg-card border-2 p-5 shadow-lg" style={{ borderColor: `${accent}33` }} data-testid="recap-score">
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">Score</div>
              <div
                className="text-3xl sm:text-4xl font-black tabular-nums"
                style={{ color: accent, fontFamily: "'Source Code Pro', monospace" }}
              >
                {stats.okRatio != null ? `${stats.okRatio}%` : '—'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">Critères OK</div>
              <div className="text-3xl sm:text-4xl font-black tabular-nums">
                {stats.okCount}<span className="text-muted-foreground">/{stats.totalTested}</span>
              </div>
            </div>
            <div className="text-center">
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">Passages</div>
              <div className="text-3xl sm:text-4xl font-black tabular-nums">
                {state.passages || 0}
              </div>
            </div>
          </div>
        </div>

        {/* Objectif */}
        {concis?.objectifCourt && (
          <section className="rounded-2xl bg-card border border-border p-5">
            <div className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest mb-2" style={{ color: accent }}>
              <Target className="w-3.5 h-3.5" /> Objectif de la séance
            </div>
            <p className="text-lg sm:text-xl font-black italic leading-snug" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              « {concis.objectifCourt} »
            </p>
          </section>
        )}

        {/* Repère visuel */}
        {Schema && (
          <section className="rounded-2xl bg-card border border-border p-5">
            <div className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest mb-2" style={{ color: accent }}>
              <BookOpen className="w-3.5 h-3.5" /> Repère visuel
            </div>
            <div className="aspect-[200/120] w-full">
              <Schema accent={accent} />
            </div>
          </section>
        )}

        {/* Phrase clé */}
        {phraseClef && (
          <section
            className="rounded-2xl p-5"
            style={{ background: `${accent}10`, borderLeft: `4px solid ${accent}` }}
          >
            <div className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest mb-2" style={{ color: accent }}>
              <Quote className="w-3.5 h-3.5" /> À retenir
            </div>
            <p className="text-base font-bold italic leading-snug">« {phraseClef} »</p>
          </section>
        )}

        {/* Acquis */}
        {okList.length > 0 && (
          <section
            className="rounded-2xl p-5"
            style={{ background: 'rgba(22,163,74,0.08)', border: '2px solid rgba(22,163,74,0.3)' }}
            data-testid="recap-ok"
          >
            <h2 className="flex items-center gap-2 text-sm font-black uppercase tracking-wider mb-3 text-[#16a34a]">
              <CheckCircle2 className="w-4 h-4" /> Acquis ({okList.length})
            </h2>
            <ul className="space-y-1.5">
              {okList.map(({ c, i }) => (
                <li key={i} className="flex items-start gap-2 text-sm leading-snug">
                  <CheckCircle2 className="w-4 h-4 text-[#16a34a] flex-shrink-0 mt-0.5" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* À retravailler */}
        {koList.length > 0 && (
          <section
            className="rounded-2xl p-5"
            style={{ background: 'rgba(220,38,38,0.08)', border: '2px solid rgba(220,38,38,0.3)' }}
            data-testid="recap-ko"
          >
            <h2 className="flex items-center gap-2 text-sm font-black uppercase tracking-wider mb-3 text-[#dc2626]">
              <XCircle className="w-4 h-4" /> À retravailler ({koList.length})
            </h2>
            <ul className="space-y-1.5">
              {koList.map(({ c, i }) => (
                <li key={i} className="flex items-start gap-2 text-sm leading-snug">
                  <XCircle className="w-4 h-4 text-[#dc2626] flex-shrink-0 mt-0.5" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Notes */}
        {(state.briefingNotes || state.guidageNotes) && (
          <section className="rounded-2xl bg-card border border-border p-5" data-testid="recap-notes">
            <h2 className="flex items-center gap-2 text-sm font-black uppercase tracking-wider mb-3" style={{ color: accent }}>
              <PenLine className="w-4 h-4" /> Notes de séance
            </h2>
            {state.briefingNotes && (
              <div className="mb-3">
                <div className="text-[10px] font-black uppercase tracking-wider text-muted-foreground mb-1">Briefing</div>
                <p className="text-sm leading-relaxed whitespace-pre-line">{state.briefingNotes}</p>
              </div>
            )}
            {state.guidageNotes && (
              <div>
                <div className="text-[10px] font-black uppercase tracking-wider text-muted-foreground mb-1">Guidage</div>
                <p className="text-sm leading-relaxed whitespace-pre-line">{state.guidageNotes}</p>
              </div>
            )}
          </section>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            type="button"
            onClick={handleExportPdf}
            className="gap-2 text-white font-black uppercase tracking-wider"
            style={{ background: accent }}
            data-testid="recap-export-pdf"
          >
            <FileDown className="w-4 h-4" /> Télécharger en PDF
          </Button>
          <Button asChild variant="outline" className="gap-2" data-testid="recap-livret">
            <Link to={`/livret#${code}`}>
              <ListChecks className="w-4 h-4" /> Mon livret REMC
            </Link>
          </Button>
        </div>

        {/* Footer info */}
        <p className="text-[11px] italic text-muted-foreground text-center pt-6">
          Récap partagé par ton moniteur · Code 2 Roues · {formatDateFr(state.updatedAt || new Date().toISOString())}
        </p>
      </div>
    </div>
  );
}
